import { ChangeDetectionStrategy, Component, computed, input, signal, inject } from '@angular/core';
import { DataService } from '../../services/data.service';
import { SessionService } from '../../services/session.service';
import {
  computeGroupRollup,
  categoryToRollupInput,
  countOverBudgetBuckets,
  type GroupRollup,
} from '../category-group-rollup';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { BudgetBulkEditRequest, GroupService } from '../../services/group.service';
import { ConfirmDialogComponent } from '../../dialog-templates/confirm-dialog/confirm-dialog.component';
import { MonthService } from '../../services/month.service';
import { AnalyticsService } from '../../services/analytics.service';
import {
  EVENT_REORDERED_CATEGORY,
  EVENT_REORDERED_BUDGET,
  EVENT_RENAMED_CATEGORY,
  EVENT_DELETED_CATEGORY,
  EVENT_DELETED_CATEGORY_CANCELED,
} from '../../analytics/events';
import { Bucket, Group, GroupPage, FormDialog } from '../../types';
import { Observable } from 'rxjs';
import { DrawerService } from '../../services/drawer.service';
import { FormDialogComponent } from '../../dialog-templates/form-dialog/form-dialog.component';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  moveItemInArray,
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  CdkDropListGroup,
  CdkDragHandle,
} from '@angular/cdk/drag-drop';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import { BucketService } from '../../services/bucket.service';
import { AsyncPipe } from '@angular/common';
import { UiInputDirective } from '../../ui/input/input.directive';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { CdkMenuTrigger, CdkMenu, CdkMenuItem } from '@angular/cdk/menu';
import {
  UiDropdownMenuDirective,
  UiDropdownMenuItemDirective,
  UiDropdownMenuTriggerDirective,
} from '../../ui/menu/menu.directive';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiIconButtonComponent } from '../../ui/icon-button/icon-button.component';
import { CategoryBucketListComponent } from '../category-bucket-list/category-bucket-list.component';
import { BudgetBulkEditService } from '../../services/budget-bulk-edit.service';

interface CategoryListItem extends Group {
  _pendingCreate?: boolean;
}

@Component({
  selector: 'app-categories-list',
  templateUrl: './categories-list.component.html',
  styleUrl: './categories-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDropList,
    CdkDrag,
    CdkDropListGroup,
    UiInputDirective,
    ReactiveFormsModule,
    FormsModule,
    UiButtonDirective,
    UiIconButtonComponent,
    CdkMenuTrigger,
    UiDropdownMenuTriggerDirective,
    CdkMenu,
    UiDropdownMenuDirective,
    CdkMenuItem,
    UiDropdownMenuItemDirective,
    CdkDragHandle,
    UiIconComponent,
    CategoryBucketListComponent,
    AsyncPipe,
  ],
})
export class CategoriesListComponent {
  private analytics = inject(AnalyticsService);
  groupService = inject(GroupService);
  private dialog = inject(UiDialogService);
  private month = inject(MonthService);
  drawerService = inject(DrawerService);
  private bucketService = inject(BucketService);
  bulkEdit = inject(BudgetBulkEditService);
  private data = inject(DataService);
  private session = inject(SessionService);

  readonly categories = input<CategoryListItem[]>([]);
  readonly monthYear = input<string | null>(null);
  readonly reordering = input(false);
  readonly activeMenuId = signal<string | null>(null);
  readonly expandedCategoryIds = signal<Record<string, boolean>>({});
  // Weak-keyed so recomputation happens only when the category object identity changes.
  private readonly rollupCache = new WeakMap<Group, GroupRollup>();

  readonly categoryRingCircumference = 59.69;
  readonly connectedListIds = computed(() => this.renderedCategories().map(category => category.id));
  readonly bulkEditBudgetsMutation = injectMutation(() => this.groupService.bulkEditBudgetsMutationOptions());
  readonly createCategoryMutation = injectMutation(() => this.groupService.createGroupMutationOptions());
  readonly deleteCategoryMutation = injectMutation(() => this.groupService.deleteGroupMutationOptions());

  async persistReorderRenames(): Promise<void> {
    const monthYear = this.monthYear() ?? this.month.monthYearFromDate();
    if (!this.bulkEdit.hasChanges()) return;

    const renamedCategory = this.bulkEdit.changedGroupRenames().length > 0;
    const reorderedCategory = this.bulkEdit.pendingGroupReorders().length > 0;
    const reorderedBucket = this.bulkEdit.pendingBucketReorders().length > 0;

    await this.bulkEditBudgetsMutation.mutateAsync(this.buildBulkEditRequest(monthYear));
    this.afterBulkEditPersist({
      renamedCategory,
      reorderedCategory,
      reorderedBucket,
    });
  }

  private buildBulkEditRequest(monthYear: string): BudgetBulkEditRequest {
    const categories = this.renderedCategories().map((category, groupIndex) => {
      const groupId = this.isPendingGroup(category) ? undefined : category.id;
      const categoryReorder = this.bulkEdit
        .pendingGroupReorders()
        .find(reorder => reorder.groupId === category.id);
      return {
        clientId: category.id,
        ...(groupId === undefined ? {} : { id: groupId }),
        name: this.isPendingGroup(category) ? category.name : this.bulkEdit.savedGroupName(category),
        sortIndex: categoryReorder?.sortIndex ?? groupIndex,
        buckets: this.visibleBucketsForBulkEdit(category).map((bucket, bucketIndex) => {
          const update = this.bulkEdit.changedBucketUpdates().find(bucketUpdate => bucketUpdate.id === bucket.id);
          const pendingArchive = this.bulkEdit.isPendingArchive(bucket.id);
          const bucketReorder = this.bulkEdit.pendingBucketReorders().find(reorder => reorder.bucketId === bucket.id);
          return {
            clientId: bucket.id,
            ...(bucket._pendingCreate ? {} : { id: bucket.id }),
            name: update?.name ?? bucket.name,
            amount: update?.amount ?? bucket.amount,
            type: this.toBudgetBulkEditBucketType(bucket.type),
            sortIndex: bucketReorder?.sortIndex ?? bucketIndex,
            ...(pendingArchive ? { archive: true } : {}),
            ...(bucket.isSetup ? { isSetup: true } : {}),
          };
        }),
      };
    });

    return {
      monthYear,
      groups: categories,
      archives: this.bulkEdit.pendingArchives().map(archive => ({
        bucketId: archive.bucketId,
        ...(archive.transferBalanceBucketId == null
          ? {}
          : { transferBalanceBucketClientId: archive.transferBalanceBucketId }),
      })),
      unarchives: this.bulkEdit.pendingUnarchives(),
    };
  }

  categorySortUpdate = (event: CdkDragDrop<Group[]>): void => {
    if (event.previousIndex === event.currentIndex) return;
    const category = event.item.data;
    const previousIndex = this.categories().findIndex(item => item.id === category.id);
    const currentIndex = this.categoryDropIndex(event.currentIndex);
    if (previousIndex < 0 || previousIndex === currentIndex) return;
    moveItemInArray(this.categories(), previousIndex, currentIndex);
    this.bulkEdit.reorderGroup(category.id, currentIndex);
    this.bulkEdit.syncGroupOrder(this.categories().map(item => item.id));
  };

  constructor() {
    this.expandedCategoryIds.set(this.readPersistedExpansion());
  }

  categoryTrackBy(_index: number, category: CategoryListItem): string {
    return category.id;
  }

  categoryHeaderClass(): string {
    const verticalSpacing = this.reordering() ? 'py-1.5' : 'py-category-group-y';
    return [
      'flex',
      'items-center',
      'gap-4',
      'border-b',
      'border-border-muted',
      'px-category-group-x',
      verticalSpacing,
    ].join(' ');
  }

  closeMenu(): void {
    this.activeMenuId.set(null);
  }

  isMenuOpen(groupId: string): boolean {
    return this.activeMenuId() === groupId;
  }

  confirmDeleteCategory(category: Group): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `Are you sure you want to delete the category <b>${category.name}</b>?${
          category.buckets.length > 0 ? `<br><br>Your budgets will be moved to Uncategorized.` : ''
        }`,
        cancelText: 'Cancel',
        confirmText: 'Delete',
        confirmColor: 'warn',
        onConfirm: () => this.deleteGroup(category.id),
        onClose: () => this.analytics.sendEvent(EVENT_DELETED_CATEGORY_CANCELED),
      },
    });
  }

  deleteGroup(id: string): void {
    this.deleteCategoryMutation.mutate(
      { id, monthYear: this.monthYear() },
      {
        onSuccess: () => {
          this.bucketService.invalidateAllBuckets();
          this.analytics.sendEvent(EVENT_DELETED_CATEGORY);
        },
      },
    );
  }

  getBudgeted(category: GroupPage): number {
    return category.budget;
  }

  getCategoryRollup(category: Group): GroupRollup {
    const cached = this.rollupCache.get(category);
    if (cached) return cached;
    const rollup = computeGroupRollup(categoryToRollupInput(category));
    this.rollupCache.set(category, rollup);
    return rollup;
  }

  overBudgetCount(category: Group): number {
    return countOverBudgetBuckets(category.buckets);
  }

  isExpanded(category: CategoryListItem): boolean {
    if (this.reordering()) return true;
    return this.expandedCategoryIds()[category.id] ?? false;
  }

  toggleCategory(category: CategoryListItem): void {
    if (this.reordering()) return;
    this.expandedCategoryIds.update(expanded => ({ ...expanded, [category.id]: !this.isExpanded(category) }));
    this.persistExpansion();
  }

  allRenderedExpanded(): boolean {
    const categories = this.renderedCategories().filter(category => category.buckets.length > 0);
    return categories.length > 0 && categories.every(category => this.isExpanded(category));
  }

  toggleAll(): void {
    const target = !this.allRenderedExpanded();
    const expanded = this.renderedCategories().reduce<Record<string, boolean>>((acc, category) => {
      if (category.buckets.length > 0) acc[category.id] = target;
      return acc;
    }, {});
    this.expandedCategoryIds.set(expanded);
    this.persistExpansion();
  }

  /**
   * Group expansion is a per-device view preference — persisted in localStorage
   * (persist: true exempts it from cache pruning), keyed by account so a shared
   * browser doesn't leak one account's layout to another. Stale ids for removed
   * groups are harmless: unknown ids fall back to collapsed.
   */
  private expansionStorageKey(): string | null {
    const accountId = this.session.persistedSession?.account?.id;
    return accountId ? `budgets.category-groups-expanded.${accountId}` : null;
  }

  private readPersistedExpansion(): Record<string, boolean> {
    const key = this.expansionStorageKey();
    if (!key) return {};
    const stored = this.data.getLocalStorage<Record<string, boolean>>(key);
    return stored && typeof stored === 'object' ? stored : {};
  }

  private persistExpansion(): void {
    const key = this.expansionStorageKey();
    if (!key) return;
    // Only remember groups left expanded; collapsed is the default.
    const expandedOnly = Object.fromEntries(Object.entries(this.expandedCategoryIds()).filter(([, isOpen]) => isOpen));
    this.data.setLocalStorage(key, expandedOnly, { persist: true });
  }

  getCategoryProgressOffset(category: Group): number {
    const percent = this.getCategoryRollup(category).ringPercent ?? 0;
    return this.categoryRingCircumference * (1 - Math.min(percent, 100) / 100);
  }

  getCategoryProgressColor(category: Group): string {
    switch (this.getCategoryRollup(category).ringTone) {
      case 'danger':
        return 'var(--app-budget-ring-danger)';
      case 'warn':
        return 'var(--app-budget-ring-warn)';
      default:
        return 'var(--app-budget-ring-ok)';
    }
  }

  getCategoryProgressTrackColor(category: Group): string {
    switch (this.getCategoryRollup(category).ringTone) {
      case 'danger':
        return 'var(--app-budget-ring-danger-track)';
      case 'warn':
        return 'var(--app-budget-ring-warn-track)';
      default:
        return 'var(--app-budget-ring-ok-track)';
    }
  }

  getCategoryFigureColor(category: Group): string {
    switch (this.getCategoryRollup(category).figure.tone) {
      case 'income':
        return 'var(--app-text-recurring-income-pill)';
      case 'warn':
        return 'var(--app-color-status-warn)';
      case 'danger':
        return 'var(--app-color-status-danger)';
      default:
        return 'var(--app-text-title)';
    }
  }

  get currentMonthYear$(): Observable<string> {
    return this.month.currentMonthYear$;
  }

  archiveCandidates(): Bucket[] {
    return this.categories()
      .flatMap(category => category.buckets)
      .filter(bucket => !bucket.archivedMonth)
      .filter(bucket => !this.bulkEdit.isPendingArchive(bucket.id));
  }

  renderedCategories(): CategoryListItem[] {
    return [
      ...this.bulkEdit.pendingGroups().map(group => ({
        id: group.id,
        name: group.name,
        buckets: [] as Bucket[],
        budgeted: { BUDGET: 0, INCOME: 0 },
        balance: { BUDGET: 0, INCOME: 0 },
        fundBalance: { BUDGET: 0, INCOME: 0 },
        createdByUserId: '',
        sortIndex: this.categories().length,
        _pendingCreate: true,
      })),
      ...this.categories(),
    ] as CategoryListItem[];
  }

  updateGroupName(category: CategoryListItem, name: string): void {
    if (this.isPendingGroup(category)) this.bulkEdit.updatePendingGroup(category.id, name);
    else this.bulkEdit.renameGroup(category.id, name);
  }

  removePendingGroup(category: CategoryListItem): void {
    this.bulkEdit.removePendingGroup(category.id);
  }

  isPendingGroup(category: CategoryListItem): boolean {
    return category._pendingCreate === true;
  }

  private visibleBucketsForBulkEdit(category: CategoryListItem): Bucket[] {
    return [
      ...category.buckets,
      ...this.bulkEdit.pendingBucketsForGroup(category.id).map(bucket => ({
        id: bucket.id,
        groupId: bucket.groupId,
        amount: bucket.amount,
        createdByUserId: '',
        createdMonth: bucket.monthYear,
        monthYear: bucket.monthYear,
        name: bucket.name,
        sortIndex: 0,
        status: 'ACTIVE',
        total: 0,
        transactions: {
          data: [] as never[],
          pageNumber: 1,
          pageSize: 25,
          totalRecords: 0,
          params: { pageNumber: 1, pageSize: 25 },
          _objectType: 'transactionPage',
        },
        type: bucket.type,
        isSetup: false,
        _objectType: 'bucket',
        _pendingCreate: true,
      })),
    ].filter(
      (bucket: Bucket) =>
        !bucket.archivedMonth ||
        this.bulkEdit.isPendingUnarchive(bucket.id) ||
        this.bulkEdit.isPendingArchive(bucket.id),
    );
  }

  private toBudgetBulkEditBucketType(type: string): 'INCOME' | 'BUDGET' | 'SAVINGS' | 'TRANSFERS' {
    if (type === 'INCOME' || type === 'SAVINGS' || type === 'TRANSFERS') return type;
    return 'BUDGET';
  }

  private afterBulkEditPersist({
    renamedCategory,
    reorderedCategory,
    reorderedBucket,
  }: {
    renamedCategory: boolean;
    reorderedCategory: boolean;
    reorderedBucket: boolean;
  }): void {
    this.bucketService.invalidateAllBuckets();
    if (renamedCategory) this.analytics.sendEvent(EVENT_RENAMED_CATEGORY);
    if (reorderedCategory) this.analytics.sendEvent(EVENT_REORDERED_CATEGORY);
    if (reorderedBucket) this.analytics.sendEvent(EVENT_REORDERED_BUDGET);
  }

  private categoryDropIndex(renderedDropIndex: number): number {
    return this.renderedCategories()
      .slice(0, renderedDropIndex)
      .filter(category => !this.isPendingGroup(category)).length;
  }

  private getAddCategoryName(form: NgForm): string | null {
    const inputValue = form.controls['inputValue']?.value;
    if (typeof inputValue !== 'string' || inputValue.trim() === '') return null;
    return inputValue;
  }

  openAddCategoryDialog(): void {
    this.closeMenu();
    const data: FormDialog = {
      title: 'Create a new group.',
      formType: 'textInput',
      submitText: 'Add',
      form: {
        placeholder: 'Add New Group',
        maxlength: '24',
      },
      onSubmit: (form: NgForm) => {
        const name = this.getAddCategoryName(form);
        if (!name) return;
        this.addNewCategory(name);
      },
    };

    this.dialog.open(FormDialogComponent, {
      data,
    });
  }

  addNewCategory(name: string): void {
    this.createCategoryMutation.mutate(
      { name, monthYear: this.monthYear() },
      {
        onSuccess: () => this.bucketService.invalidateAllBuckets(),
      },
    );
  }
}
